"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Allowed hosts that can be indexed by search engines.
 * Only vocly.fm and www.vocly.fm are allowed.
 */
const ALLOWED_HOSTS = new Set([
  "vocly.fm",
  "www.vocly.fm",
]);

/**
 * Check if a hostname is allowed to be indexed.
 * Returns true only for vocly.fm and www.vocly.fm.
 */
function isIndexableHost(hostname: string): boolean {
  // Remove port if present (e.g., "vocly.fm:3000" -> "vocly.fm")
  const hostWithoutPort = hostname.split(":")[0];
  return ALLOWED_HOSTS.has(hostWithoutPort);
}

/**
 * Client component that adds a canonical link tag to the document head.
 * For non-allowed hosts, points to https://vocly.fm with the same path.
 * For allowed hosts, no canonical link is added (browsers will use the current URL).
 */
export function CanonicalLink() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  
  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;
    
    const hostname = window.location.host;
    const isIndexable = isIndexableHost(hostname);
    
    // Remove any existing canonical link
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    if (!isIndexable) {
      // Build the canonical URL with the current path and search params
      const search = searchParams.toString();
      const fullPath = pathname + (search ? `?${search}` : "");
      const canonicalUrl = `https://vocly.fm${fullPath}`;
      
      // Create and add the canonical link
      const link = document.createElement("link");
      link.rel = "canonical";
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }
    
    // Cleanup function to remove the link when component unmounts
    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.remove();
      }
    };
  }, [pathname, searchParams]);
  
  // This component doesn't render anything
  return null;
}
